// Pure helpers for the Standings page — position of the current user and
// whether the full standings should be shown (scrollable) versus the small
// window around the user.
//
// Kept as a standalone module (no React / no Supabase imports) so the logic
// can be exercised by simple Node-based unit tests, since the project does
// not ship a JS/TS test runner.

export interface PositionAwareRow {
  user_id: number;
  rank?: number | null;
  // Score fields used as a tiebreaker fallback when `rank` is missing.
  totgoals?: number;
  totassists?: number;
  totgoals_goalie?: number;
  totassists_goalie?: number;
  totwins_goalie?: number;
  totshutout_goalie?: number;
  tototlosses_goalie?: number;
}

export interface UserPosition {
  /** 1-based position of the user in the ordered standings, or null if not found. */
  position: number | null;
  /** Total number of teams in the standings. */
  total: number;
}

function totalPoints(row: PositionAwareRow): number {
  return (
    (row.totgoals ?? 0) +
    (row.totassists ?? 0) +
    (row.totgoals_goalie ?? 0) +
    (row.totassists_goalie ?? 0) +
    (row.totwins_goalie ?? 0) +
    (row.totshutout_goalie ?? 0) +
    (row.tototlosses_goalie ?? 0)
  );
}

/**
 * Compute the 1-based position of the current user inside a standings array
 * plus the total number of teams. Matches the ordering used by
 * `StandingsTable`:
 *
 *  - if any row exposes a numeric `rank`, rows are ordered by `rank` ascending
 *    and the user's rank is used directly;
 *  - otherwise rows are sorted by descending total points (same sum as the
 *    "Total" column rendered in the table).
 *
 * Returns { position: null, total: rows.length } when the user is not present
 * (typical for a non-authenticated viewer).
 */
export function computeUserPosition(
  rows: readonly PositionAwareRow[] | null | undefined,
  userId: number | null | undefined,
): UserPosition {
  if (!rows || rows.length === 0) {
    return { position: null, total: 0 };
  }

  const total = rows.length;

  if (userId === null || userId === undefined) {
    return { position: null, total };
  }

  const numericUserId = Number(userId);

  const hasRank = rows.some((r) => typeof r.rank === 'number');

  if (hasRank) {
    const found = rows.find((r) => Number(r.user_id) === numericUserId);
    if (found && typeof found.rank === 'number') {
      return { position: found.rank, total };
    }
    return { position: null, total };
  }

  const sorted = rows.slice().sort((a, b) => totalPoints(b) - totalPoints(a));
  const idx = sorted.findIndex((r) => Number(r.user_id) === numericUserId);
  return { position: idx >= 0 ? idx + 1 : null, total };
}

/**
 * Format the "Ma position : X sur Y" string, delegating to the caller for
 * translation of the labels.
 *
 * Returns null when the user has no rank yet (e.g. team not built).
 */
export function formatMyPosition(
  position: number | null,
  total: number,
  labels: { my: string; of: string },
): string | null {
  if (!position || total <= 0) return null;
  return `${labels.my} ${position} ${labels.of} ${total}`;
}

// ---------------------------------------------------------------------------
// Navigation dans la fenêtre de classement (embed Arsenal).
//
// Les RPCs `get_standings_window` / `get_monthly_standings_window` renvoient
// les rangs [user_rank - p_before, user_rank + p_after]. Pour afficher une
// fenêtre arbitraire [start, start + size - 1] sans nouvelle RPC, on calcule
// des offsets relatifs au rang de l'utilisateur (ils peuvent être négatifs).
// ---------------------------------------------------------------------------

export const STANDINGS_WINDOW_RADIUS = 5;
export const STANDINGS_WINDOW_SIZE = STANDINGS_WINDOW_RADIUS * 2 + 1;
export const STANDINGS_WINDOW_STEP = 10;

/** Premier rang d'une fenêtre centrée sur `rank` (jamais sous 1). */
export function windowStartAround(rank: number, radius = STANDINGS_WINDOW_RADIUS): number {
  return Math.max(1, Math.floor(rank) - radius);
}

/** Offsets `before` / `after` à passer aux RPCs pour obtenir [start, start + size - 1]. */
export function windowOffsetsForStart(
  userRank: number,
  start: number,
  size = STANDINGS_WINDOW_SIZE,
): { before: number; after: number } {
  const safeStart = Math.max(1, Math.floor(start));
  return {
    before: userRank - safeStart,
    after: safeStart + size - 1 - userRank,
  };
}
